import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Leaf, Target, HeartHandshake } from "lucide-react";
import GlassCard from "./ui/GlassCard";

const backgroundImages = [
  "/images/background/IMG_6459.JPG",
  "/images/background/IMG_6461.JPG",
];

const VALUES = [
  "Экологическая ответственность",
  "Социальная солидарность",
  "Прозрачность и доверие",
  "Партнёрство",
  "Обучение и развитие",
];

const GOALS = [
  "Реабилитация уязвимых групп",
  "Развитие экосознания",
  "Образовательные проекты",
  "Озеленение и экоакции",
];

export default function AboutPage() {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % backgroundImages.length);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-dark-50">
      {/* Header band */}
      <div className="relative overflow-hidden pt-32 pb-20 px-6">
        {/* Background photo */}
        <div className="absolute inset-0">
          {backgroundImages.map((img, i) => (
            <div
              key={img}
              className="absolute inset-0 bg-cover bg-center transition-opacity duration-1000"
              style={{ backgroundImage: `url(${img})`, opacity: i === currentImageIndex ? 1 : 0 }}
            />
          ))}
          <div className="absolute inset-0 bg-gradient-hero opacity-80" />
        </div>

        <div className="pointer-events-none absolute -top-24 -left-24 w-72 h-72 bg-primary-400/30 rounded-full blur-3xl animate-float-slow" />
        <div className="pointer-events-none absolute -bottom-24 -right-16 w-80 h-80 bg-teal-400/20 rounded-full blur-3xl animate-float" />

        <div className="relative max-w-4xl mx-auto text-center">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-1.5 mb-4 text-sm font-semibold tracking-wide uppercase rounded-full bg-white/15 text-white backdrop-blur-sm"
          >
            <Leaf className="w-4 h-4" />
            О нас
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-3xl md:text-4xl font-bold text-white"
          >
            ОО «Жасыл Желкен»
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="mt-3 text-white/80 max-w-xl mx-auto"
          >
            Объединяем неравнодушных граждан, стремящихся к зелёному, справедливому и
            устойчивому будущему.
          </motion.p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-6 -mt-10 pb-20 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <GlassCard hover={false} className="p-6 md:p-10 bg-white text-center">
            <p className="text-dark-700 text-base md:text-lg leading-relaxed max-w-3xl mx-auto">
              <strong className="text-dark-900">«Жасыл желкен»</strong> — общественное
              объединение, объединяющее неравнодушных граждан, стремящихся к созданию{" "}
              <span className="text-primary-600 font-semibold">
                зелёного, справедливого и устойчивого
              </span>{" "}
              будущего.
            </p>
            <p className="mt-4 text-dark-500 max-w-3xl mx-auto">
              Мы реализуем проекты в сфере экологии, реабилитации и просвещения.
              Присоединяйтесь — станьте частью перемен.
            </p>
          </GlassCard>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6 mt-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <GlassCard hover={false} className="p-6 md:p-8 bg-white h-full">
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-primary-500 to-teal-500 flex items-center justify-center shadow-glow mb-4">
                <HeartHandshake className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-lg font-bold text-dark-900 mb-2">Миссия</h2>
              <p className="text-dark-500 text-sm leading-relaxed">
                Создаём пространство надежды и роста — через заботу о человеке и природе.
              </p>
            </GlassCard>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <GlassCard hover={false} className="p-6 md:p-8 bg-white h-full">
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-primary-500 to-teal-500 flex items-center justify-center shadow-glow mb-4">
                <Leaf className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-lg font-bold text-dark-900 mb-2">Ценности</h2>
              <ul className="space-y-1.5">
                {VALUES.map((v) => (
                  <li key={v} className="text-dark-500 text-sm flex items-start gap-2">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-primary-400 shrink-0" />
                    {v}
                  </li>
                ))}
              </ul>
            </GlassCard>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <GlassCard hover={false} className="p-6 md:p-8 bg-white h-full">
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-primary-500 to-teal-500 flex items-center justify-center shadow-glow mb-4">
                <Target className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-lg font-bold text-dark-900 mb-2">Цели</h2>
              <ul className="space-y-1.5">
                {GOALS.map((g) => (
                  <li key={g} className="text-dark-500 text-sm flex items-start gap-2">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-primary-400 shrink-0" />
                    {g}
                  </li>
                ))}
              </ul>
            </GlassCard>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
